import { useEffect, useRef } from "react";

/**
 * Smart Polling Hook
 * Polls a function at regular intervals, but only when:
 * - The page is visible (not in background tab)
 * - Polling is enabled
 * 
 * @param {Function} callback - Function to call on each poll
 * @param {number} interval - Polling interval in milliseconds
 * @param {boolean} enabled - Whether polling is enabled
 */
export const usePolling = (callback, interval = 10000, enabled = true) => {
  const savedCallback = useRef();
  const intervalId = useRef();

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    // Don't set up polling if disabled
    if (!enabled) {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
      return;
    }

    // Function to check if page is visible
    const tick = () => {
      if (document.visibilityState === "visible") {
        savedCallback.current?.();
      }
    };

    // Set up interval
    intervalId.current = setInterval(tick, interval);

    // Run immediately on mount
    tick();

    // Cleanup
    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, [interval, enabled]);
};